import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Download, CheckCircle, Clock, XCircle, AlertCircle,
  Upload, Send, MessageSquare, ChevronDown, ChevronUp, Eye, Paperclip,
  DollarSign, Printer, Phone, History, RotateCcw, Receipt
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { useAuth } from '../App.jsx';
import AsignarIndiceModal from '../components/AsignarIndiceModal.jsx';
import SubirDocumentosModal from '../components/SubirDocumentosModal.jsx';
import Pagination from '../components/Pagination.jsx';

// Importación de submódulos (PascalCase)
import FiltrosBusqueda from '../components/GestionSolicitudes/FiltrosBusqueda';
import HistorialPanel from '../components/GestionSolicitudes/HistorialPanel';
import ModalCorreo from '../components/GestionSolicitudes/ModalCorreo';
import ModalCotizacion from '../components/GestionSolicitudes/ModalCotizacion';
import DevolucionSolicitudModal from '../components/DevolucionSolicitudModal.jsx';
import SubirBoletaModal from '../components/SubirBoletaModal.jsx';
import FacturacionCardInfo from '../components/FacturacionCardInfo.jsx';
import { handleImprimirSolicitud } from '../utils/printSolicitud';

/* ─── Helpers ──────────────────────────────────────────────────────────── */
function getBadgeClass(estadoNombre) {
  const txt = (estadoNombre || '').toLowerCase();
  if (txt.includes('pendiente correo')) return 'badge-warning';
  if (txt.includes('ingresado'))         return 'badge-info';
  if (txt.includes('proces'))           return 'badge-copper';
  if (txt.includes('recibido'))         return 'badge-info'; // Comprobante Recibido
  if (txt.includes('confirmado'))       return 'badge-success'; // Pago Confirmado
  if (txt.includes('pago'))             return 'badge-warning'; // Pendiente Pago
  if (txt.includes('complet'))         return 'badge-success';
  if (txt.includes('rechaz'))          return 'badge-error';
  return 'badge-gray';
}

/* ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────── */
export default function GestionSolicitudes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const canAssign = hasRole('super_admin, admin, boss');
  
  const [solicitudes, setSolicitudes]       = useState([]);
  const [estados, setEstados]               = useState([]);
  const [cargando, setCargando]             = useState(true);
  const [searchTerm, setSearchTerm]         = useState(searchParams.get('search') || '');

  useEffect(() => {
    const s = searchParams.get('search');
    if (s !== null) {
      setSearchTerm(s);
    }
  }, [searchParams]);

  const [currentPage, setCurrentPage]       = useState(1);
  const [expandedId, setExpandedId]         = useState(null);
  const [expandedDetail, setExpandedDetail] = useState({});
  const [itemParaAsignar, setItemParaAsignar] = useState(null);
  const [rechazarData, setRechazarData]     = useState(null);
  const [motivoRechazo, setMotivoRechazo]   = useState('');
  
  const [modalCorreo, setModalCorreo]       = useState(null);
  const [modalSubirDocs, setModalSubirDocs] = useState(null);
  const [modalCotizacion, setModalCotizacion] = useState(null);
  const [devolucionModal, setDevolucionModal] = useState({ isOpen: false, solicitud: null });
  const [boletaModal, setBoletaModal]         = useState({ isOpen: false, item: null });
  
  const [docOriginal, setDocOriginal]       = useState({});
  const [usuarios, setUsuarios]             = useState([]);
  
  const [mostrarSoloActivas, setMostrarSoloActivas] = useState(searchParams.get('activas') === 'false' ? false : true);
  const [mostrarSoloMias, setMostrarSoloMias] = useState(searchParams.get('mias') === 'true');
  const [filtroEstadoId, setFiltroEstadoId] = useState('');
  const [mostrarSoloNuevos, setMostrarSoloNuevos] = useState(false);
  const [pagination, setPagination]         = useState({ current_page: 1, last_page: 1, total: 0 });

  useEffect(() => {
    const estadoParam = searchParams.get('estado');
    if (estados.length > 0 && estadoParam) {
      const found = estados.find(e => e.nombre.toLowerCase() === estadoParam.toLowerCase());
      if (found) {
        setFiltroEstadoId(found.id);
        if (estadoParam.toLowerCase() === 'completado' || estadoParam.toLowerCase() === 'rechazado') {
          setMostrarSoloActivas(false);
        }
      }
    }
  }, [estados, searchParams]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [solRes, estRes, usrRes] = await Promise.all([
        api.get('/solicitudes'),
        api.get('/estados'),
        api.get('/users/assignable'),
      ]);
      setSolicitudes(solRes.data.data || []);
      setEstados(estRes.data.filter(e => e.tipo === 'solicitud'));
      setUsuarios(usrRes.data.data || []);
    } catch {
      toast.error('Error al cargar datos base.');
    } finally {
      setCargando(false);
    }
  };

  const fetchSolicitudes = async (clearExpandId = null, page = 1) => {
    try {
      setCargando(true);
      const { data } = await api.get('/solicitudes', {
        params: {
          page,
          search: searchTerm,
          estado_id: filtroEstadoId,
          exclude_final: mostrarSoloActivas ? 1 : 0,
          mine: mostrarSoloMias ? 1 : 0,
          unread_only: mostrarSoloNuevos ? 1 : 0
        }
      });
      
      setSolicitudes(data.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        total: data.total
      });
      setCurrentPage(data.current_page);

      if (clearExpandId) {
        setExpandedDetail(prev => {
          const next = { ...prev };
          delete next[clearExpandId];
          return next;
        });
        const sol = (data.data || []).find(s => s.id === clearExpandId);
        if (sol) cargarDetalle(sol);
      }
    } catch {
      toast.error('Error al recargar la lista.');
    } finally {
      setCargando(false);
    }
  };

  // Efecto para búsqueda con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSolicitudes(null, 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, filtroEstadoId, mostrarSoloActivas, mostrarSoloMias, mostrarSoloNuevos]);

  const handlePageChange = (newPage) => {
    fetchSolicitudes(null, newPage);
  };

  const cargarDocOriginal = async (solicitud) => {
    if (!solicitud.s3_path) return;
    if (docOriginal[solicitud.id]) return;
    setDocOriginal(prev => ({ ...prev, [solicitud.id]: 'loading' }));
    try {
      const { data } = await api.get(`/solicitudes/${solicitud.id}/documento-original`);
      setDocOriginal(prev => ({ ...prev, [solicitud.id]: data }));
    } catch {
      setDocOriginal(prev => ({ ...prev, [solicitud.id]: 'error' }));
    }
  };

  const cargarDetalle = async (solicitud) => {
    try {
      const { data } = await api.get(`/solicitudes/${solicitud.id}`);
      setExpandedDetail(prev => ({ ...prev, [solicitud.id]: { ...data.solicitud, comprobante_url: data.comprobante_url } }));
    } catch {
      setExpandedDetail(prev => ({ ...prev, [solicitud.id]: solicitud }));
    }
  };

  const toggleExpand = (solicitud) => {
    if (expandedId === solicitud.id) {
      setExpandedId(null);
    } else {
      setExpandedId(solicitud.id);
      cargarDocOriginal(solicitud);
      cargarDetalle(solicitud);

      if (solicitud.has_unread_reply) {
        setSolicitudes(prev => prev.map(s =>
          s.id === solicitud.id ? { ...s, has_unread_reply: false } : s
        ));
      }
    }
  };

  const cambiarEstado = async (solId, estadoId) => {
    try {
      await api.patch(`/solicitudes/${solId}/estado`, { estado_id: estadoId });
      toast.success('Estado actualizado');
      fetchSolicitudes();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const handleEnviarEntrega = async (solicitud) => {
    try {
      toast.loading('Enviando entrega...', { id: 'entrega' });
      await api.post(`/solicitudes/${solicitud.id}/enviar-entrega`);
      toast.success('Documentos enviados al ciudadano.', { id: 'entrega' });
      fetchSolicitudes();
    } catch {
      toast.error('Error al enviar documentos.', { id: 'entrega' });
    }
  };

  const rechazarSolicitud = async () => {
    if (!motivoRechazo.trim()) { toast.error('El motivo es obligatorio'); return; }
    try {
      toast.loading('Procesando...', { id: 'rechazar' });
      await api.post(`/solicitudes/${rechazarData.id}/rechazar`, { motivo: motivoRechazo });
      toast.success('Solicitud rechazada. Notificado por correo.', { id: 'rechazar' });
      setRechazarData(null); setMotivoRechazo('');
      fetchSolicitudes();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al rechazar.', { id: 'rechazar' });
    }
  };

  const handleDescargarDoc = (doc) => {
    const API = import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl';
    window.open(`${API}/api/v1/solicitudes/documentos/${doc.token_descarga}/descargar`, '_blank');
  };

  const handleDownload = (doc) => {
    const API = import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl';
    window.open(`${API}/api/v1/documentos/${doc.token_descarga}/descargar`, '_blank');
  };

  const filteredSolicitudes = solicitudes;

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px)' }}>
      {/* Header */}
      <div className="animate-fade-in" style={{ marginBottom: '1.5rem' }}>
        <h2 className="section-title">Gestión de Solicitudes</h2>
        <span className="accent-line" />
        <p className="section-subtitle">
          Administre las solicitudes enviadas por los ciudadanos, adjunte documentos y mantenga comunicación directa.
        </p>
      </div>

      <FiltrosBusqueda 
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        estados={estados}
        filtroEstadoId={filtroEstadoId} setFiltroEstadoId={setFiltroEstadoId}
        mostrarSoloNuevos={mostrarSoloNuevos} setMostrarSoloNuevos={setMostrarSoloNuevos}
        mostrarSoloMias={mostrarSoloMias} setMostrarSoloMias={setMostrarSoloMias}
        mostrarSoloActivas={mostrarSoloActivas} setMostrarSoloActivas={setMostrarSoloActivas}
      />

      {/* Lista */}
      {cargando ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}><span className="spinner spinner-lg" /></div>
      ) : filteredSolicitudes.length === 0 ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📥</div>
          <h3>Sin resultados</h3>
          <p className="text-muted">No se encontraron solicitudes con esos criterios.</p>
        </div>
      ) : (
        <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {filteredSolicitudes.map(solicitud => (
            <div key={solicitud.id} className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>

              {/* Fila resumen (clic para expandir) */}
              <div
                style={{
                  padding: '0.85rem 1.2rem', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', cursor: 'pointer',
                  background: expandedId === solicitud.id ? '#f8fafc' : 'transparent',
                }}
                onClick={() => toggleExpand(solicitud)}
              >
                <div>
                  <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', marginBottom: '0.2rem' }}>
                    <span style={{ fontWeight: 800, color: 'var(--primary-dark)', fontSize: '0.95rem' }}>
                      {solicitud.nombre_solicitante}
                    </span>
                    <span className={`badge ${getBadgeClass(solicitud.estado?.nombre)}`}
                      style={{ textTransform: 'uppercase', padding: '2px 8px', fontSize: '0.63rem', borderRadius: 4 }}>
                      {solicitud.estado?.nombre || 'Desconocido'}
                    </span>
                    {solicitud.has_unread_reply && (
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{width: 6, height: 6, background: '#ef4444', borderRadius: '50%', display: 'inline-block'}}></span>
                        Nueva Respuesta
                      </span>
                    )}
                    {solicitud.s3_path && (
                      <span title="Tiene documento adjunto" style={{ color: 'var(--copper)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Paperclip size={13} /> Doc
                      </span>
                    )}
                    {solicitud.documentos?.length > 0 && (
                      <span style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 600 }}>
                        +{solicitud.documentos.length} archivo(s)
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    RUT: {solicitud.rut_solicitante} &nbsp;·&nbsp;
                    {solicitud.telefono && <><Phone size={11} style={{ verticalAlign: 'middle' }} /> {solicitud.telefono} &nbsp;·&nbsp;</>}
                    {solicitud.numero_seguimiento || 'Sin número'} &nbsp;·&nbsp;
                    {new Date(solicitud.created_at).toLocaleDateString('es-CL')}
                    {solicitud.assigned_to
                      ? <span style={{ marginLeft: 6, color: '#b87333', fontWeight: 600 }}>· 👤 {solicitud.assigned_to.name}</span>
                      : <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontStyle: 'italic' }}>· Sin asignar</span>
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    {solicitud.items?.length || 0} ítems
                  </span>
                  {expandedId === solicitud.id ? <ChevronUp size={18} color="var(--primary)" /> : <ChevronDown size={18} color="var(--primary)" />}
                </div>
              </div>

              {/* Panel expandido */}
              {expandedId === solicitud.id && (
                <div style={{ borderTop: '1px solid var(--border-color)', background: '#f8fafc' }}>
                  
                  {/* Banner de Rechazo si aplica */}
                  {solicitud.estado?.nombre === 'Rechazado' && (
                    <div style={{
                      margin: '1rem 1.2rem 0',
                      padding: '12px 16px',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      <XCircle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#991b1b' }}>
                          Solicitud Rechazada
                        </div>
                        <div style={{ fontSize: '0.84rem', color: '#7f1d1d', marginTop: '3px', whiteSpace: 'pre-wrap' }}>
                          <strong>Motivo de rechazo:</strong> {solicitud.motivo_rechazo || 'Sin detalle de motivo registrado.'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Acciones de la solicitud ───────────────────────── */}
                  <div style={{ padding: '1rem 1.2rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.83rem' }}>
                      <strong>Email:</strong> {solicitud.email} &nbsp;
                      {solicitud.telefono && <><strong>Tel:</strong> {solicitud.telefono} &nbsp;</>}
                      {solicitud.rut_boleta && <><strong>RUT Boleta:</strong> {solicitud.rut_boleta} &nbsp;</>}
                      <strong>Entrega:</strong> {solicitud.forma_entrega}
                      {solicitud.observaciones && <>&nbsp;·&nbsp;<span style={{ color: 'var(--warning)' }}>{solicitud.observaciones}</span></>}
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {canAssign && (
                        <select
                          value={solicitud.asignado_a || ''}
                          onChange={async e => {
                            try {
                              await api.patch(`/solicitudes/${solicitud.id}/asignar`, { asignado_a: e.target.value || null });
                              toast.success(e.target.value ? 'Solicitud asignada.' : 'Asignación removida.');
                              fetchSolicitudes();
                            } catch (e) {
                              toast.error(e.response?.data?.error || 'Error al asignar.');
                            }
                          }}
                          style={{ padding: '5px 10px', fontSize: '0.82rem', borderRadius: 6, borderColor: solicitud.asignado_a ? '#b87333' : 'var(--border-color)', color: solicitud.asignado_a ? '#b87333' : 'inherit', fontWeight: solicitud.asignado_a ? 600 : 400 }}
                        >
                          <option value="">👤 Sin asignar</option>
                          {usuarios.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      )}
                      
                      <select value={solicitud.estado_id || ''}
                        onChange={e => cambiarEstado(solicitud.id, e.target.value)}
                        style={{ padding: '5px 28px 5px 10px', fontSize: '0.82rem', borderRadius: 6 }}>
                        <option value="" disabled>Cambiar Estado...</option>
                        {estados.filter(e => e.nombre !== 'Rechazado').map(est => (
                          <option key={est.id} value={est.id}>{est.nombre}</option>
                        ))}
                      </select>

                      {solicitud.estado?.nombre === 'Completado' ? (
                        <button className="btn btn-sm" style={{ background: '#64748b', color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}
                          onClick={() => handleEnviarEntrega(solicitud)}>
                          <Send size={14} /> Reenviar correo completo
                        </button>
                      ) : solicitud.estado?.nombre === 'Pago Confirmado' ? (
                        <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}
                          onClick={() => handleEnviarEntrega(solicitud)}>
                          <Send size={14} /> Enviar Documentos
                        </button>
                      ) : (
                        <button className="btn btn-sm" style={{ background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}
                          onClick={() => setModalCorreo(solicitud)}>
                          <Send size={14} /> Correo
                        </button>
                      )}

                      <button className="btn btn-sm"
                        style={{ background: '#059669', color: '#fff', display: 'flex', alignItems: 'center', gap: 5, border: 'none' }}
                        onClick={() => setModalCotizacion(solicitud)}>
                        <DollarSign size={14} /> Cotizar
                      </button>

                      {/* Botón Subir/Ver Boleta (en Comprobante Pago Recibido, Pago Confirmado, Procesando o Completado) */}
                      {(solicitud.estado?.nombre === 'Pago Confirmado' || solicitud.estado?.nombre === 'Completado' || solicitud.estado?.nombre === 'Procesando' || solicitud.estado?.nombre === 'Comprobante Pago Recibido' || solicitud.boleta_path || solicitud.numero_boleta || (solicitud.total_cotizacion > 0)) && (() => {
                        const tieneBoleta = Boolean(solicitud.boleta_path || solicitud.numero_boleta);
                        const numBol = solicitud.numero_boleta;
                        return (
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: tieneBoleta ? '#ecfdf5' : '#eff6ff',
                              color: tieneBoleta ? '#065f46' : '#1d4ed8',
                              border: `1px solid ${tieneBoleta ? '#a7f3d0' : '#bfdbfe'}`,
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              fontWeight: 600
                            }}
                            title={tieneBoleta ? 'Ver o actualizar boleta tributaria' : 'Subir boleta tributaria del pago'}
                            onClick={() => setBoletaModal({ isOpen: true, item: solicitud })}
                          >
                            <Receipt size={14} color={tieneBoleta ? '#059669' : '#2563eb'} />
                            {tieneBoleta ? (numBol ? `Boleta N° ${numBol}` : 'Boleta Adjunta') : 'Subir Boleta'}
                          </button>
                        );
                      })()}
                      
                      <button className="btn btn-sm"
                        style={{ background: '#475569', color: '#fff', display: 'flex', alignItems: 'center', gap: 5, border: 'none' }}
                        onClick={() => handleImprimirSolicitud(solicitud)}>
                        <Printer size={14} /> Imprimir
                      </button>

                      <button className="btn btn-sm"
                        style={{ background: '#b87333', color: '#fff', display: 'flex', alignItems: 'center', gap: 5, border: 'none' }}
                        onClick={() => navigate(`/solicitud/historial/${solicitud.id}`, { state: { from: '/gestion-solicitudes' } })}>
                        <History size={14} /> Historial
                      </button>

                      <button className="btn btn-sm" style={{ background: 'var(--error)', color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}
                        onClick={() => setRechazarData(solicitud)}
                        disabled={solicitud.estado?.nombre === 'Rechazado'}>
                        <XCircle size={14} /> Rechazar
                      </button>

                      {(solicitud.total_cotizacion > 0 && solicitud.estado?.nombre !== 'Devuelto') && (
                        <button className="btn btn-sm btn-danger" style={{ background: 'var(--error)', color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}
                          onClick={() => setDevolucionModal({ isOpen: true, solicitud })}>
                          <RotateCcw size={14} /> Devolución
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Ficha de Facturación / Boleta */}
                  <div style={{ padding: '0.85rem 1.2rem', borderBottom: '1px solid var(--border-color)', background: '#f8fafc' }}>
                    <FacturacionCardInfo data={solicitud} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                    {/* COLUMNA IZQUIERDA: Ítems */}
                    <div style={{ padding: '1rem 1.2rem', borderRight: '1px solid var(--border-color)' }}>
                      <h4 style={{ margin: '0 0 0.8rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FileText size={15} /> Documentos Solicitados
                      </h4>
                      <div className="table-wrapper">
                        <table style={{ background: 'var(--bg-surface)', fontSize: '0.8rem' }}>
                          <thead>
                            <tr>
                              <th>Documento</th>
                              <th>F/N/A</th>
                              <th>Registro</th>
                            </tr>
                          </thead>
                          <tbody>
                            {solicitud.items?.map(item => (
                              <tr key={item.id}>
                                <td style={{ fontWeight: 600 }}>{item.tipo_documento}</td>
                                <td className="text-muted">{item.foja || '-'}/{item.numero || '-'}/{item.anio || '-'}</td>
                                <td>
                                  {item.registro_indice ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      <div style={{ fontSize: '0.75rem', color: 'var(--success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <CheckCircle size={14} /> Asignado
                                      </div>
                                      <div style={{ background: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.15)', borderRadius: '6px', padding: '4px 6px', fontSize: '0.75rem' }}>
                                        <div style={{ fontWeight: 700, color: 'var(--primary-dark)' }}>
                                          {item.registro_indice.intervinientes && item.registro_indice.intervinientes.length > 0 ? (
                                            item.registro_indice.intervinientes.map(inv =>
                                              [inv.nombres, inv.apellido_paterno, inv.apellido_materno].filter(Boolean).join(' ')
                                            ).join(' / ')
                                          ) : 'Sin intervinientes'}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: '2px' }}>
                                          F: {item.registro_indice.foja} | N: {item.registro_indice.numero} | {item.registro_indice.tipo_registro}
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                        {item.registro_indice.documentos?.length > 0 ? (
                                          item.registro_indice.documentos.map(doc => (
                                            <button key={doc.id} className="icon-btn icon-btn-view" onClick={() => handleDownload(doc)} title="Descargar PDF" style={{ padding: '3px 6px', gap: '4px', fontSize: '0.7rem' }}>
                                              <Download size={12} /> PDF
                                            </button>
                                          ))
                                        ) : (
                                          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Sin PDF</span>
                                        )}
                                        <button className="btn btn-sm" onClick={() => setItemParaAsignar(item)} style={{ background: 'transparent', border: '1px dotted var(--border-color)', fontSize: '0.7rem', padding: '2px 6px' }}>
                                          Cambiar
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button className="btn btn-sm" style={{ padding: '4px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}
                                      onClick={() => setItemParaAsignar(item)}>
                                      <FileText size={12} /> Asignar
                                    </button>
                                  )}
                                  {item.registro || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* COLUMNA DERECHA: Documentos + Historial */}
                    <div style={{ padding: '1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {/* Documento original del ciudadano */}
                      <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Paperclip size={15} /> Documento del Ciudadano
                        </h4>
                        {!solicitud.s3_path ? (
                          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No adjuntó documentos.</p>
                        ) : docOriginal[solicitud.id] === 'loading' ? (
                          <span className="spinner" style={{ width: 18, height: 18 }} />
                        ) : docOriginal[solicitud.id] === 'error' ? (
                          <p style={{ fontSize: '0.8rem', color: 'var(--error)' }}>Error al cargar los documentos.</p>
                        ) : Array.isArray(docOriginal[solicitud.id]) && docOriginal[solicitud.id].length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {docOriginal[solicitud.id].map((doc, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                                <FileText size={16} color="var(--success)" flexShrink={0} />
                                <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {doc.nombre_archivo}
                                </span>
                                <a href={doc.url} target="_blank" rel="noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600, flexShrink: 0 }}>
                                  <Eye size={14} /> Ver
                                </a>
                                <a href={doc.url} download={doc.nombre_archivo}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--copper)', fontWeight: 600, flexShrink: 0 }}>
                                  <Download size={14} /> Descargar
                                </a>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Cargando...</p>
                        )}
                      </div>

                      {/* Comprobante de Pago */}
                      {expandedDetail[solicitud.id]?.comprobante_url && (
                        <div>
                          <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <DollarSign size={15} /> Comprobante de Pago
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#e0e7ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                            <FileText size={16} color="var(--primary)" flexShrink={0} />
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              comprobante_pago
                            </span>
                            <a href={expandedDetail[solicitud.id].comprobante_url} target="_blank" rel="noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600, flexShrink: 0 }}>
                              <Eye size={14} /> Ver
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Boleta Electrónica */}
                      {(solicitud.boleta_path || solicitud.numero_boleta || expandedDetail[solicitud.id]?.solicitud?.boleta_path || expandedDetail[solicitud.id]?.solicitud?.numero_boleta) && (
                        <div>
                          <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Receipt size={15} color="#16a34a" /> Boleta Electrónica
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                              <Receipt size={16} color="#16a34a" flexShrink={0} />
                              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#166534', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                Boleta {solicitud.numero_boleta || expandedDetail[solicitud.id]?.solicitud?.numero_boleta ? `N° ${solicitud.numero_boleta || expandedDetail[solicitud.id]?.solicitud?.numero_boleta}` : 'Adjunta'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              {(solicitud.boleta_url || expandedDetail[solicitud.id]?.solicitud?.boleta_url || solicitud.boleta_path) && (
                                <a 
                                  href={solicitud.boleta_url || expandedDetail[solicitud.id]?.solicitud?.boleta_url || `/api/v1/solicitudes/${solicitud.id}/boleta`} 
                                  target="_blank" 
                                  rel="noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: '#16a34a', fontWeight: 600, textDecoration: 'none', background: 'white', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: 4 }}>
                                  <Eye size={13} /> Ver
                                </a>
                              )}
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setBoletaModal({ isOpen: true, item: solicitud })}
                                style={{
                                  padding: '3px 8px',
                                  fontSize: '0.78rem',
                                  color: '#1e40af',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  background: 'white',
                                  border: '1px solid #bfdbfe',
                                  borderRadius: 4,
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                                title="Editar o actualizar boleta"
                              >
                                Editar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Documentos adicionales (staff) */}
                      <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Upload size={15} /> Documentos
                        </h4>

                        {!expandedDetail[solicitud.id] ? (
                          <span className="spinner" style={{ width: 16, height: 16 }} />
                        ) : expandedDetail[solicitud.id].documentos?.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                            {expandedDetail[solicitud.id].documentos.map(doc => (
                              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.7rem', background: '#fff', borderRadius: 7, border: '1px solid var(--border-color)', fontSize: '0.8rem' }}>
                                <FileText size={13} color="var(--copper)" flexShrink={0} />
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nombre_original}</span>
                                <button className="icon-btn" onClick={() => handleDescargarDoc(doc)} title="Descargar" style={{ padding: '4px 8px', flexShrink: 0 }}>
                                  <Download size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Sin documentos.</p>
                        )}

                        <div style={{ marginTop: '0.5rem' }}>
                          <button 
                            className="btn btn-sm" 
                            style={{ background: '#f8fafc', color: 'var(--primary-dark)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 1rem' }}
                            onClick={() => setModalSubirDocs(solicitud)}
                          >
                            <Upload size={15} /> Subir Archivos
                          </button>
                        </div>
                      </div>

                      {/* Historial */}
                      <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MessageSquare size={15} /> Historial de Comunicaciones
                        </h4>
                        {!expandedDetail[solicitud.id] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                            <span className="spinner" style={{ width: 16, height: 16 }} /> Cargando historial...
                          </div>
                        ) : (
                          <HistorialPanel
                            steps={expandedDetail[solicitud.id].workflow_steps || []}
                            onReply={(step) => setModalCorreo({ item: solicitud, replyTo: step })}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <Pagination 
            currentPage={pagination.current_page} 
            totalItems={pagination.total} 
            itemsPerPage={20} 
            onPageChange={handlePageChange} 
          />
        </div>
      )}

      {/* Modal Rechazar */}
      {rechazarData && (
        <div className="modal-overlay" onClick={() => setRechazarData(null)}>
          <div className="modal-content" style={{ maxWidth: 450 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '1.1rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={20} /> Rechazar Solicitud
              </h2>
              <button className="modal-close" onClick={() => setRechazarData(null)}><XCircle size={18} /></button>
            </div>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Rechazarás la solicitud de <strong>{rechazarData.nombre_solicitante}</strong>. El motivo será enviado al ciudadano por correo.
            </p>
            <div className="form-group">
              <label>Motivo del Rechazo</label>
              <textarea rows={4} value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)}
                placeholder="Ej: Faltan datos para procesar la solicitud..." />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn btn-secondary" onClick={() => setRechazarData(null)}>Cancelar</button>
              <button className="btn" style={{ background: 'var(--error)', color: '#fff' }} onClick={rechazarSolicitud}>Confirmar Rechazo</button>
            </div>
          </div>
        </div>
      )}

      {modalCorreo && (
        <ModalCorreo
          item={modalCorreo.item || modalCorreo}
          replyTo={modalCorreo.replyTo}
          onClose={() => setModalCorreo(null)}
          onSent={fetchSolicitudes}
        />
      )}

      {itemParaAsignar && (
        <AsignarIndiceModal
          item={itemParaAsignar}
          onClose={() => setItemParaAsignar(null)}
          onAsignado={() => { setItemParaAsignar(null); fetchSolicitudes(); }}
        />
      )}

      {modalSubirDocs && (
        <SubirDocumentosModal
          solicitud={modalSubirDocs}
          onClose={() => setModalSubirDocs(null)}
          onSuccess={() => {
            setModalSubirDocs(null);
            fetchSolicitudes(modalSubirDocs.id);
          }}
        />
      )}

      {modalCotizacion && (
        <ModalCotizacion
          solicitud={modalCotizacion}
          onClose={() => setModalCotizacion(null)}
          onUpdate={fetchSolicitudes}
        />
      )}

      {devolucionModal.isOpen && (
        <DevolucionSolicitudModal
          solicitud={devolucionModal.solicitud}
          onClose={() => setDevolucionModal({ isOpen: false, solicitud: null })}
          onUpdate={fetchSolicitudes}
        />
      )}

      {boletaModal.isOpen && (
        <SubirBoletaModal
          isOpen={boletaModal.isOpen}
          item={boletaModal.item}
          type="solicitud"
          onClose={() => setBoletaModal({ isOpen: false, item: null })}
          onSuccess={fetchSolicitudes}
        />
      )}
    </div>
  );
}
